import React, { useEffect, useMemo, useState } from 'react';
import { FileText, Loader2, Search } from 'lucide-react';
import { toast } from 'react-toastify';
import baseURL from '../utils/baseURL';
import useAxiosAuth from '../hooks/useAxiosAuth';
import InvoiceModal from '../components/subscription/InvoiceModal';

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : '—');
const money = (a) => (a != null && String(a) !== '' ? `€${a}` : '—');
const cap = (s) => String(s || '').replace(/^\w/, (c) => c.toUpperCase());

const TABS = [
  { id: 'all', label: 'All' },
  { id: 'paid', label: 'Paid' },
  { id: 'pending', label: 'Pending' },
];

// Admin → Subscription → Payments. Every subscription payment across doctors:
// paid rows (with a downloadable invoice, same as the doctor sees) + pending plan
// changes doctors still owe. Tabs filter by status.
export default function SubscriptionPayments() {
  const axiosInstance = useAxiosAuth();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('all');
  const [query, setQuery] = useState('');
  const [invoice, setInvoice] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const res = await axiosInstance.get(`${baseURL}/subscription-package/payments`);
        if (alive) setPayments(res.data?.payments || []);
      } catch (e) {
        if (alive) { setPayments([]); toast.warn('Failed to load payments'); }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const statusOf = (p) => p.status || 'paid';

  const counts = useMemo(() => ({
    all: payments.length,
    paid: payments.filter((p) => statusOf(p) === 'paid').length,
    pending: payments.filter((p) => statusOf(p) === 'pending').length,
  }), [payments]);

  const rows = useMemo(() => {
    let list = tab === 'all' ? payments : payments.filter((p) => statusOf(p) === tab);
    const q = query.trim().toLowerCase();
    if (q) {
      list = list.filter((p) =>
        String(p.doctorName || '').toLowerCase().includes(q) ||
        String(p.doctorId || '').toLowerCase().includes(q) ||
        String(p.packageName || '').toLowerCase().includes(q));
    }
    return list;
  }, [payments, tab, query]);

  return (
    <div className="min-h-full w-full p-2 sm:p-4" data-testid="subscription-payments-page">
      <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent">Payments</h1>
      <p className="text-sm text-slate-500 mt-1 mb-4">Subscription payments across all doctors — paid invoices and pending plan changes.</p>

      {/* Tabs + search */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {TABS.map((tb) => {
          const on = tab === tb.id;
          return (
            <button
              key={tb.id}
              type="button"
              onClick={() => setTab(tb.id)}
              data-testid={`payments-tab-${tb.id}`}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${on ? 'bg-[#4ABDBD] text-white shadow-sm' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-100'}`}
            >
              {tb.label} <span className="opacity-70">({counts[tb.id]})</span>
            </button>
          );
        })}
        <div className="relative ml-auto">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search doctor / plan"
            data-testid="payments-search"
            className="pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] w-56"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-md p-4 sm:p-5">
        {loading ? (
          <div className="flex items-center justify-center py-16" data-testid="payments-loading">
            <Loader2 className="animate-spin text-[#4ABDBD]" size={30} />
          </div>
        ) : rows.length === 0 ? (
          <p className="text-sm text-slate-500 py-10 text-center" data-testid="payments-empty">No payments in this view.</p>
        ) : (
          <div className="overflow-x-auto custom-scrollbar">
            <table className="w-full text-sm whitespace-nowrap">
              <thead>
                <tr className="text-left text-xs text-slate-400 border-b border-gray-100">
                  <th className="py-2 pr-3 font-semibold">Doctor</th>
                  <th className="py-2 pr-3 font-semibold">Plan</th>
                  <th className="py-2 pr-3 font-semibold text-right">Amount</th>
                  <th className="py-2 pr-3 font-semibold">Date</th>
                  <th className="py-2 pr-3 font-semibold">Status</th>
                  <th className="py-2 font-semibold text-right">Invoice</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => {
                  const isPaid = statusOf(p) === 'paid';
                  return (
                    <tr key={p.id} className="border-b border-gray-50 hover:bg-gray-50" data-testid={`payment-row-${p.id}`}>
                      <td className="py-2.5 pr-3">
                        <div className="text-gray-800 font-medium whitespace-normal">{p.doctorName || p.doctorId}</div>
                        <div className="text-[11px] text-slate-400 font-mono">{p.doctorId}</div>
                      </td>
                      <td className="py-2.5 pr-3 text-gray-700 whitespace-normal">{p.packageName || '—'}</td>
                      <td className="py-2.5 pr-3 text-right font-semibold text-gray-800">
                        {money(p.amount)}
                        {Number(p.creditApplied) > 0 && (
                          <span className="block text-[10px] font-normal text-emerald-600">−€{p.creditApplied} credit</span>
                        )}
                      </td>
                      <td className="py-2.5 pr-3 text-gray-600">{fmtDate(p.createdAt)}</td>
                      <td className="py-2.5 pr-3">
                        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${isPaid ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                          {cap(statusOf(p))}
                        </span>
                      </td>
                      <td className="py-2.5 text-right">
                        {p.invoiceAvailable ? (
                          <button
                            type="button"
                            onClick={() => setInvoice(p)}
                            data-testid={`view-invoice-${p.id}`}
                            className="inline-flex items-center gap-1 text-[#4ABDBD] hover:text-[#2D8A8A] font-semibold"
                          >
                            <FileText className="w-3.5 h-3.5" /> View
                          </button>
                        ) : (
                          <span className="text-slate-300 text-xs">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {invoice && (
        <InvoiceModal payment={invoice} doctorId={invoice.doctorName || invoice.doctorId} onClose={() => setInvoice(null)} />
      )}
    </div>
  );
}
